import { planzoneSettings, wbsConfiguration } from "@planzone/dto";
import { events } from "@planzone/events";
import { Boundaries } from "@planzone/core";
import { createStateReducer } from "../../utils/createStateReducer";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRetrieveWbsConfigurationRequest(
  state: State,
  planzone: Boundaries["retrieveWbsConfiguration"]["request"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToPending() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToPending(),
          configuration: wbsConfiguration,
        },
      ];
}

function handleRetrieveWbsConfigurationSuccess(
  state: State,
  {
    planzone,
    configuration,
  }: Boundaries["retrieveWbsConfiguration"]["success"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToSuccess(), configuration },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          configuration,
        },
      ];
}

function handleRetrieveWbsConfigurationFailure(
  state: State,
  planzone: Boundaries["retrieveWbsConfiguration"]["failure"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToFailure() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToFailure(),
          configuration: wbsConfiguration,
        },
      ];
}

function handleEditWbsConfigurationRequest(
  state: State,
  {
    planzone,
    nextConfiguration,
  }: Boundaries["editWbsConfiguration"]["request"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], configuration: nextConfiguration },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          configuration: nextConfiguration,
        },
      ];
}

function handleEditWbsConfigurationSuccess(
  state: State,
  { planzone, configuration }: Boundaries["editWbsConfiguration"]["success"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], configuration },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          configuration,
        },
      ];
}

function handleEditWbsConfigurationFailure(
  state: State,
  { planzone, configuration }: Boundaries["editWbsConfiguration"]["failure"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], configuration },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          configuration,
        },
      ];
}

const reduceState = createStateReducer(initialState, {
  [events.retrieveWbsConfigurationRequested]:
    handleRetrieveWbsConfigurationRequest,
  [events.retrieveWbsConfigurationSucceeded]:
    handleRetrieveWbsConfigurationSuccess,
  [events.retrieveWbsConfigurationFailed]:
    handleRetrieveWbsConfigurationFailure,
  [events.editWbsConfigurationRequested]: handleEditWbsConfigurationRequest,
  [events.editWbsConfigurationSucceeded]: handleEditWbsConfigurationSuccess,
  [events.editWbsConfigurationFailed]: handleEditWbsConfigurationFailure,
});

export type { State, Selector };

export { reduceState, initialState, createSelector };
